package com.hyperether.goodjob.util

import androidx.compose.runtime.Composable
import com.hyperether.goodjob.log
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.JobStatus
import com.hyperether.goodjob.models.Repeat
import com.hyperether.goodjob.models.WeekdayOption
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.repeat_daily_weekday
import com.hyperether.goodjob.resources.repeat_daily_workday
import com.hyperether.goodjob.resources.unassigned
import com.hyperether.goodjob.resources.unnamed_team
import com.hyperether.goodjob.scenes.scheduler.PeriodType
import com.hyperether.goodjob.util.DateTimeUtil.getDaysInMonth
import com.hyperether.planner.model.Event
import com.hyperether.planner.model.Plan
import com.hyperether.resources.stringResource
import com.hyperether.resources.stringResourcePlain
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.daysUntil
import kotlinx.datetime.minus
import kotlinx.datetime.plus

object ScheduleUtil {

    suspend fun getPlanList(
        jobs: List<Job>?,
        periodType: PeriodType,
        selectedDay: LocalDate,
        weekSelectionStart: LocalDate,
        weekSelectionEnd: LocalDate,
        monthSelectionLocalDate: LocalDate,
        listOfPlans: MutableList<Plan>,
        employeeId: String?
    ) {
        val jobsForEvents: List<Job> = filterJobsForPeriod(
            jobs = jobs,
            periodType = periodType,
            selectedDay = selectedDay,
            weekSelectionStart = weekSelectionStart,
            weekSelectionEnd = weekSelectionEnd,
            monthSelectionLocalDate = monthSelectionLocalDate
        )
        log.d("ScheduleUtil") { "getPlanList: periodType: ${periodType.name} jobsForEvents: ${jobsForEvents.size} employeeId: $employeeId" }

        jobsForEvents.forEach { job ->
            var startDateStartTime: LocalDateTime? = null
            try {
                val dt = job.startDate + "T" + job.start_at
                startDateStartTime = LocalDateTime.parse(dt)
            } catch (e: Exception) {
                return@forEach
            }
            var startDateEndTime: LocalDateTime? = null
            try {
                val dt = job.startDate + "T" + job.end_at
                startDateEndTime = LocalDateTime.parse(dt)
            } catch (e: Exception) {
                return@forEach
            }
            var dueDate: LocalDateTime? = null
            try {
                val dt = job.dueDate + "T" + job.end_at
                dueDate = LocalDateTime.parse(dt)
            } catch (e: Exception) {
//                    return@filter false
            }
            job.assignees?.let { assignees ->
                log.d("ScheduleUtil") { "getPlanList: assignees: ${assignees.employees?.size}" }
                // Process employees
                assignees.employees?.let { userList ->
                    userList.forEach { user ->
                        if (user?._id == employeeId || employeeId == null)
                            user?.getName()?.let { username ->
                                addEventForName(
                                    job = job,
                                    periodType = periodType,
                                    selectedDay = selectedDay,
                                    weekSelectionStart = weekSelectionStart,
                                    weekSelectionEnd = weekSelectionEnd,
                                    monthSelectionLocalDate = monthSelectionLocalDate,
                                    startDateStartTime = startDateStartTime,
                                    startDateEndTime = startDateEndTime,
                                    dueDateTime = dueDate,
                                    assigneeName = username,
                                    listOfPlans = listOfPlans,
                                    employeeId = employeeId
                                )
                            }
                    }
                }

                // Process teams
                assignees.teams?.let { teamList ->
                    teamList.forEach { team ->
                        val teamName = team?.name ?: stringResourcePlain(Res.string.unnamed_team)
                        addEventForName(
                            job = job,
                            periodType = periodType,
                            selectedDay = selectedDay,
                            weekSelectionStart = weekSelectionStart,
                            weekSelectionEnd = weekSelectionEnd,
                            monthSelectionLocalDate = monthSelectionLocalDate,
                            startDateStartTime = startDateStartTime,
                            startDateEndTime = startDateEndTime,
                            dueDateTime = dueDate,
                            assigneeName = teamName,
                            listOfPlans = listOfPlans,
                            employeeId = employeeId
                        )
                    }
                }

                if (assignees.employees.isNullOrEmpty() && assignees.teams.isNullOrEmpty()) {
                    addEventForName(
                        job = job,
                        periodType = periodType,
                        selectedDay = selectedDay,
                        weekSelectionStart = weekSelectionStart,
                        weekSelectionEnd = weekSelectionEnd,
                        monthSelectionLocalDate = monthSelectionLocalDate,
                        startDateStartTime = startDateStartTime,
                        startDateEndTime = startDateEndTime,
                        dueDateTime = dueDate,
                        assigneeName = stringResourcePlain(Res.string.unassigned),
                        listOfPlans = listOfPlans,
                        employeeId = employeeId
                    )
                }
            } ?: kotlin.run {
                if (job.assignees?.employees.isNullOrEmpty() && job.assignees?.teams.isNullOrEmpty()) {
                    addEventForName(
                        job = job,
                        periodType = periodType,
                        selectedDay = selectedDay,
                        weekSelectionStart = weekSelectionStart,
                        weekSelectionEnd = weekSelectionEnd,
                        monthSelectionLocalDate = monthSelectionLocalDate,
                        startDateStartTime = startDateStartTime,
                        startDateEndTime = startDateEndTime,
                        dueDateTime = dueDate,
                        assigneeName = stringResourcePlain(Res.string.unassigned),
                        listOfPlans = listOfPlans,
                        employeeId = employeeId
                    )
                }
            }
        }
    }

    fun filterEmployeeJobs(
        userName: String,
        listOfPlans: MutableList<Plan>
    ) {
        val uniqueEventsMap = mutableMapOf<String?, Event>()
        for (plan in listOfPlans) {
            for (event in plan.events) {
                uniqueEventsMap[event.jobId] = event
            }
        }
        val eventList = uniqueEventsMap.values.toMutableList()
        listOfPlans.clear()
        listOfPlans.add(Plan(userName, eventList))
    }

    fun filterJobsForPeriod(
        jobs: List<Job>?,
        periodType: PeriodType,
        selectedDay: LocalDate,
        weekSelectionStart: LocalDate,
        weekSelectionEnd: LocalDate,
        monthSelectionLocalDate: LocalDate
    ): List<Job> {
        val jobsForEvents: List<Job> =
            jobs?.filter { job ->
                var startDateStartTime: LocalDateTime? = null
                try {
                    val dt = job.startDate + "T" + job.start_at
                    startDateStartTime = LocalDateTime.parse(dt)
                } catch (e: Exception) {
                    return@filter false
                }
                var startDateEndTime: LocalDateTime? = null
                try {
                    val dt = job.startDate + "T" + job.end_at
                    startDateEndTime = LocalDateTime.parse(dt)
                } catch (e: Exception) {
                    return@filter false
                }

                var dueDate: LocalDateTime? = null
                try {
                    val dt = job.dueDate + "T" + job.end_at
                    dueDate = LocalDateTime.parse(dt)
                } catch (e: Exception) {
//                    return@filter false
                }

                when (periodType) {
                    PeriodType.DAY -> {
                        if (dueDate != null) {
                            startDateStartTime.date <= selectedDay && selectedDay <= dueDate.date
                        } else {
                            when (job.repeat) {
                                Repeat.never.name -> {
                                    startDateStartTime.date == selectedDay
                                }

                                Repeat.every_day.name,
                                Repeat.every_week.name,
                                Repeat.every_month.name,
                                Repeat.every_2_weeks.name,
                                Repeat.custom.name -> {
                                    startDateStartTime.date <= selectedDay
                                }

                                else -> {
                                    false
                                }
                            }
                        }
                    }

                    PeriodType.WEEK -> {
                        if (dueDate != null) {
                            (startDateStartTime.date <= weekSelectionEnd) &&
                                    (dueDate.date >= weekSelectionStart)
                        } else {
                            when (job.repeat) {
                                Repeat.never.name -> {
                                    startDateStartTime.date in weekSelectionStart..weekSelectionEnd
                                }

                                Repeat.every_day.name,
                                Repeat.every_week.name,
                                Repeat.every_month.name,
                                Repeat.every_2_weeks.name,
                                Repeat.custom.name -> {
                                    startDateStartTime.date <= weekSelectionEnd
                                }

                                else -> false
                            }
                        }
                    }

                    PeriodType.MONTH -> {
                        val monthStart = LocalDate(
                            monthSelectionLocalDate.year,
                            monthSelectionLocalDate.monthNumber,
                            1
                        )
                        val daysInMonth = getDaysInMonth(
                            monthSelectionLocalDate.year,
                            monthSelectionLocalDate.monthNumber
                        )
                        val monthEnd = LocalDate(
                            monthSelectionLocalDate.year,
                            monthSelectionLocalDate.monthNumber,
                            daysInMonth
                        )

                        if (dueDate != null) {
                            startDateStartTime.date <= monthEnd &&
                                    dueDate.date >= monthStart
                        } else {
                            when (job.repeat) {
                                Repeat.never.name -> {
                                    startDateStartTime.month == monthSelectionLocalDate.month
                                }

                                Repeat.every_day.name,
                                Repeat.every_week.name,
                                Repeat.every_month.name,
                                Repeat.every_2_weeks.name,
                                Repeat.custom.name -> {
                                    startDateStartTime.year < monthSelectionLocalDate.year || startDateStartTime.month <= monthSelectionLocalDate.month
                                }

                                else -> false
                            }
                        }
                    }
                }
            }?.map {
                it
            } ?: run {
                emptyList()
            }

        return jobsForEvents
    }

    private fun addEventForName(
        job: Job,
        periodType: PeriodType,
        selectedDay: LocalDate,
        weekSelectionStart: LocalDate,
        weekSelectionEnd: LocalDate,
        monthSelectionLocalDate: LocalDate,
        startDateStartTime: LocalDateTime,
        startDateEndTime: LocalDateTime,
        dueDateTime: LocalDateTime?,
        assigneeName: String,
        listOfPlans: MutableList<Plan>,
        employeeId: String?
    ) {
        log.d("ScheduleUtil") { "addEventForName: job: ${job.jobTitle} repeat: ${job.repeatInterval} assigneeName: $assigneeName" }
        when (periodType) {
            PeriodType.DAY -> {
                when (job.repeat) {
                    Repeat.never.name -> {
                        addEventForAssignee(
                            job,
                            startDateStartTime,
                            startDateEndTime,
                            assigneeName,
                            listOfPlans,
                            allowOverlap = false,
                            employeeId,
                            true
                        )
                    }

                    Repeat.every_day.name -> {
                        if (job.repeatInterval?.isNotEmpty() == true) {
                            val end = job.repeatInterval!![0]
                            log.d("ScheduleUtil") { "addEventForName: job: ${job.jobTitle} repeat ends: $end assigneeName: $assigneeName" }
                            when (end) {
                                WeekdayOption.EVERY_WEEKDAY.stringRes -> {
                                    addEventForAssignee(
                                        job,
                                        startDateStartTime,
                                        startDateEndTime,
                                        assigneeName,
                                        listOfPlans,
                                        allowOverlap = false,
                                        employeeId,
                                        true
                                    )
                                }

                                WeekdayOption.EVERY_WORKING_DAY.stringRes -> {
                                    if (DateTimeUtil.isWorkingDay(selectedDay)) {
                                        addEventForAssignee(
                                            job,
                                            startDateStartTime,
                                            startDateEndTime,
                                            assigneeName,
                                            listOfPlans,
                                            allowOverlap = false,
                                            employeeId,
                                            true
                                        )
                                    }
                                }
                            }
                        } else {
                            addEventForAssignee(
                                job,
                                startDateStartTime,
                                startDateEndTime,
                                assigneeName,
                                listOfPlans,
                                allowOverlap = false,
                                employeeId,
                                true
                            )
                        }
                    }

                    Repeat.every_week.name -> {
                        var eventDateStartTime = startDateStartTime
                        var eventDateEndTime = startDateEndTime
                        if (eventDateStartTime.date <= selectedDay) {
                            val daysBetween = eventDateStartTime.date.daysUntil(selectedDay)
                            val isWeekBetween = daysBetween % 7 == 0
                            if (isWeekBetween) {
                                val weekBetween = daysBetween / 7
                                eventDateStartTime =
                                    DateTimeUtil.getLocalDateTimeAhead(
                                        eventDateStartTime,
                                        weekBetween * 7
                                    )
                                eventDateEndTime =
                                    DateTimeUtil.getLocalDateTimeAhead(
                                        eventDateEndTime,
                                        weekBetween * 7
                                    )

                                var endDate = dueDateTime?.date
                                if (endDate == null || endDate >= selectedDay)
                                    addEventForAssignee(
                                        job,
                                        eventDateStartTime,
                                        eventDateEndTime,
                                        assigneeName,
                                        listOfPlans,
                                        allowOverlap = false,
                                        employeeId,
                                        true
                                    )
                            }
                        }
                    }

                    Repeat.every_2_weeks.name -> {
                        var endDate = dueDateTime?.date
                        if (endDate == null || endDate >= selectedDay) {
                            var eventDateStartTime = startDateStartTime
                            var eventDateEndTime = startDateEndTime
                            if (eventDateStartTime.date <= selectedDay) {
                                val daysBetween = eventDateStartTime.date.daysUntil(selectedDay)
                                val is2WeekBetween = daysBetween % (7 * 2) == 0
                                if (is2WeekBetween) {
                                    val weekBetween = daysBetween / (7 * 2)
                                    eventDateStartTime =
                                        DateTimeUtil.getLocalDateTimeAhead(
                                            eventDateStartTime,
                                            weekBetween * 7 * 2
                                        )
                                    eventDateEndTime =
                                        DateTimeUtil.getLocalDateTimeAhead(
                                            eventDateEndTime,
                                            weekBetween * 7 * 2
                                        )

                                    if (endDate == null || eventDateStartTime.date <= endDate)
                                        addEventForAssignee(
                                            job,
                                            eventDateStartTime,
                                            eventDateEndTime,
                                            assigneeName,
                                            listOfPlans,
                                            allowOverlap = false,
                                            employeeId,
                                            true
                                        )
                                }
                            }
                        }
                    }

                    Repeat.every_month.name -> {
                        var endDate = dueDateTime?.date
                        if (endDate == null || endDate >= selectedDay) {
                            var eventDateStartTime = startDateStartTime
                            var eventDateEndTime = startDateEndTime
                            if (eventDateStartTime.date <= selectedDay) {
                                val monthDiff =
                                    selectedDay.month.ordinal - startDateStartTime.month.ordinal
                                if (monthDiff >= 0) {
                                    eventDateStartTime = DateTimeUtil.getLocalDateTimeMonthsAhead(
                                        eventDateStartTime, monthDiff
                                    )
                                    eventDateEndTime = DateTimeUtil.getLocalDateTimeMonthsAhead(
                                        eventDateEndTime, monthDiff
                                    )

                                    if (eventDateStartTime.dayOfMonth == startDateStartTime.dayOfMonth
                                        && eventDateStartTime.date == selectedDay
                                    ) {
                                        if (endDate == null || eventDateStartTime.date <= endDate)
                                            addEventForAssignee(
                                                job,
                                                eventDateStartTime,
                                                eventDateEndTime,
                                                assigneeName,
                                                listOfPlans,
                                                allowOverlap = false,
                                                employeeId,
                                                true
                                            )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            PeriodType.WEEK -> {
                when (job.repeat) {
                    Repeat.never.name -> {
                        addEventForAssignee(
                            job,
                            startDateStartTime,
                            startDateEndTime,
                            assigneeName,
                            listOfPlans,
                            employeeId = employeeId,
                            mergeEvents = false
                        )
                    }

                    Repeat.every_day.name -> {
                        var eventDateStartTime = startDateStartTime
                        var eventDateEndTime = startDateEndTime
                        if (eventDateStartTime.date < weekSelectionStart) {
                            val dayDiff = weekSelectionStart - eventDateStartTime.date
                            val daysDiff =
                                DateTimeUtil.parseDurationToDays(dayDiff, eventDateStartTime.date)
                            eventDateStartTime =
                                DateTimeUtil.getLocalDateTimeAhead(eventDateStartTime, daysDiff)
                            eventDateEndTime =
                                DateTimeUtil.getLocalDateTimeAhead(eventDateEndTime, daysDiff)
                        }

                        var lastDayOfWeek = weekSelectionEnd
                        if (job.repeatInterval?.isNotEmpty() == true) {
                            val end = job.repeatInterval!![0]
                            when (end) {
                                WeekdayOption.EVERY_WEEKDAY.stringRes -> {

                                }

                                WeekdayOption.EVERY_WORKING_DAY.stringRes -> {
                                    lastDayOfWeek =
                                        DateTimeUtil.getLastWorkingDayOfWeek(weekSelectionStart)
                                }
                            }
                        }

                        var endDate = dueDateTime?.date
                        if (endDate == null || endDate > lastDayOfWeek)
                            endDate = lastDayOfWeek
                        endDate.let {
                            var currentDate = eventDateStartTime.date
                            while (currentDate <= it) {
                                addEventForAssignee(
                                    job,
                                    eventDateStartTime,
                                    eventDateEndTime,
                                    assigneeName,
                                    listOfPlans,
                                    employeeId = employeeId,
                                    mergeEvents = false
                                )
                                eventDateStartTime =
                                    DateTimeUtil.getLocalDateTimeAhead(eventDateStartTime, 1)
                                eventDateEndTime =
                                    DateTimeUtil.getLocalDateTimeAhead(eventDateEndTime, 1)
                                currentDate = currentDate.plus(1, DateTimeUnit.DAY)
                            }
                        }
                    }

                    Repeat.every_week.name -> {
                        var eventDateStartTime = startDateStartTime
                        var eventDateEndTime = startDateEndTime
                        if (eventDateStartTime.date < weekSelectionStart) {
                            val daysBetween = eventDateStartTime.date.daysUntil(weekSelectionEnd)
                            val weekBetween = daysBetween / 7

                            eventDateStartTime =
                                DateTimeUtil.getLocalDateTimeAhead(
                                    eventDateStartTime,
                                    weekBetween * 7
                                )
                            eventDateEndTime =
                                DateTimeUtil.getLocalDateTimeAhead(
                                    eventDateEndTime,
                                    weekBetween * 7
                                )
                        }
                        var endDate = dueDateTime?.date
                        if (endDate == null || endDate >= eventDateEndTime.date)
                            addEventForAssignee(
                                job,
                                eventDateStartTime,
                                eventDateEndTime,
                                assigneeName,
                                listOfPlans,
                                employeeId = employeeId,
                                mergeEvents = false
                            )
                    }

                    Repeat.every_2_weeks.name -> {
                        var eventDateStartTime = startDateStartTime
                        var eventDateEndTime = startDateEndTime
                        val firstDayOfWeek = weekSelectionStart
                        if (eventDateStartTime.date < weekSelectionEnd) {
                            val daysBetween = eventDateStartTime.date.daysUntil(weekSelectionEnd)
                            val weekBetween = daysBetween / (7 * 2)
                            if (daysBetween < 7 || (daysBetween / 7) % 2 == 0) {
                                eventDateStartTime =
                                    DateTimeUtil.getLocalDateTimeAhead(
                                        eventDateStartTime,
                                        weekBetween * 7 * 2
                                    )
                                eventDateEndTime =
                                    DateTimeUtil.getLocalDateTimeAhead(
                                        eventDateEndTime,
                                        weekBetween * 7 * 2
                                    )
                                var endDate = dueDateTime?.date
                                if (endDate == null || endDate >= eventDateEndTime.date)
                                    addEventForAssignee(
                                        job,
                                        eventDateStartTime,
                                        eventDateEndTime,
                                        assigneeName,
                                        listOfPlans,
                                        employeeId = employeeId,
                                        mergeEvents = false
                                    )
                            }
                        }
                    }

                    Repeat.every_month.name -> {
                        var endDate = dueDateTime?.date
                        var eventDateStartTime = startDateStartTime
                        var eventDateEndTime = startDateEndTime
                        if (endDate == null || eventDateStartTime.date < weekSelectionEnd) {
                            val monthDiffStart =
                                weekSelectionStart.month.ordinal - startDateStartTime.month.ordinal
                            val monthDiffEnd =
                                weekSelectionEnd.month.ordinal - startDateStartTime.month.ordinal
                            if (monthDiffStart >= 0 || monthDiffEnd >= 0) {
                                val monthDiff = maxOf(monthDiffStart, monthDiffEnd)
                                eventDateStartTime = DateTimeUtil.getLocalDateTimeMonthsAhead(
                                    eventDateStartTime, monthDiff
                                )
                                eventDateEndTime = DateTimeUtil.getLocalDateTimeMonthsAhead(
                                    eventDateEndTime, monthDiff
                                )

                                if (eventDateStartTime.dayOfMonth == startDateStartTime.dayOfMonth &&
                                    eventDateStartTime.date > weekSelectionStart &&
                                    eventDateStartTime.date < weekSelectionEnd
                                ) {
                                    if (endDate == null || eventDateStartTime.date <= endDate)
                                        addEventForAssignee(
                                            job,
                                            eventDateStartTime,
                                            eventDateEndTime,
                                            assigneeName,
                                            listOfPlans,
                                            employeeId = employeeId,
                                            mergeEvents = false
                                        )
                                }
                            }
                        }
                    }
                }
            }

            PeriodType.MONTH -> {
                when (job.repeat) {
                    Repeat.never.name -> {
                        addEventForAssignee(
                            job,
                            startDateStartTime,
                            startDateEndTime,
                            assigneeName,
                            listOfPlans,
                            employeeId = employeeId,
                            mergeEvents = true
                        )
                    }

                    Repeat.every_day.name -> {
                        var eventDateStartTime = startDateStartTime
                        var eventDateEndTime = startDateEndTime
                        var currentDate = startDateStartTime.date
                        val dueDate = dueDateTime?.date
                        val fallbackDate = DateTimeUtil.getLastDayOfMonth(monthSelectionLocalDate)
                        val endDate = when {
                            dueDate != null && fallbackDate != null -> minOf(dueDate, fallbackDate)
                            dueDate != null -> dueDate
                            else -> fallbackDate
                        }

                        while (eventDateStartTime.month < monthSelectionLocalDate.month) {
                            eventDateStartTime =
                                DateTimeUtil.getLocalDateTimeAhead(eventDateStartTime, 1)
                            eventDateEndTime =
                                DateTimeUtil.getLocalDateTimeAhead(eventDateEndTime, 1)
                            currentDate = eventDateStartTime.date
                        }

                        while (eventDateStartTime.month > monthSelectionLocalDate.month) {
                            eventDateStartTime =
                                DateTimeUtil.getLocalDateTimeAhead(eventDateStartTime, -1)
                            eventDateEndTime =
                                DateTimeUtil.getLocalDateTimeAhead(eventDateEndTime, -1)
                            currentDate = eventDateStartTime.date
                        }

                        endDate?.let {
                            var weekDay = true
                            if (job.repeatInterval?.isNotEmpty() == true) {
                                weekDay =
                                    job.repeatInterval!![0] != WeekdayOption.EVERY_WORKING_DAY.stringRes
                            }
                            while (currentDate <= endDate) {
                                if (weekDay || DateTimeUtil.isWorkingDay(currentDate))
                                    addEventForAssignee(
                                        job,
                                        eventDateStartTime,
                                        eventDateEndTime,
                                        assigneeName,
                                        listOfPlans,
                                        employeeId = employeeId,
                                        mergeEvents = true
                                    )
                                eventDateStartTime =
                                    DateTimeUtil.getLocalDateTimeAhead(eventDateStartTime, 1)
                                eventDateEndTime =
                                    DateTimeUtil.getLocalDateTimeAhead(eventDateEndTime, 1)
                                currentDate = currentDate.plus(1, DateTimeUnit.DAY)
                            }
                        }
                    }

                    Repeat.every_week.name -> {
                        var eventDateStartTime = startDateStartTime
                        var eventDateEndTime = startDateEndTime
                        val dueDate = dueDateTime?.date
                        val fallbackDate = DateTimeUtil.getLastDayOfMonth(monthSelectionLocalDate)
                        val endDate = when {
                            dueDate != null && fallbackDate != null -> minOf(dueDate, fallbackDate)
                            dueDate != null -> dueDate
                            else -> fallbackDate
                        }

                        while (eventDateStartTime.month < monthSelectionLocalDate.month) {
                            eventDateStartTime = LocalDateTime(
                                eventDateStartTime.date.plus(1, DateTimeUnit.WEEK),
                                eventDateStartTime.time
                            )
                            eventDateEndTime = LocalDateTime(
                                eventDateEndTime.date.plus(1, DateTimeUnit.WEEK),
                                eventDateEndTime.time
                            )
                        }

                        while (eventDateStartTime.month > monthSelectionLocalDate.month) {
                            eventDateStartTime = LocalDateTime(
                                eventDateStartTime.date.plus(-1, DateTimeUnit.WEEK),
                                eventDateStartTime.time
                            )
                            eventDateEndTime = LocalDateTime(
                                eventDateEndTime.date.plus(-1, DateTimeUnit.WEEK),
                                eventDateEndTime.time
                            )
                        }

                        endDate?.let {
                            while (eventDateStartTime.date <= endDate) {
                                addEventForAssignee(
                                    job,
                                    eventDateStartTime,
                                    eventDateEndTime,
                                    assigneeName,
                                    listOfPlans,
                                    employeeId = employeeId,
                                    mergeEvents = true
                                )

                                eventDateStartTime = LocalDateTime(
                                    eventDateStartTime.date.plus(
                                        1,
                                        DateTimeUnit.WEEK
                                    ), eventDateStartTime.time
                                )
                                eventDateEndTime = LocalDateTime(
                                    eventDateEndTime.date.plus(1, DateTimeUnit.WEEK),
                                    eventDateEndTime.time
                                )
                            }
                        }
                    }

                    Repeat.every_2_weeks.name -> {
                        var eventDateStartTime = startDateStartTime
                        var eventDateEndTime = startDateEndTime
                        val dueDate = dueDateTime?.date
                        val fallbackDate = DateTimeUtil.getLastDayOfMonth(monthSelectionLocalDate)
                        val endDate = when {
                            dueDate != null && fallbackDate != null -> minOf(dueDate, fallbackDate)
                            dueDate != null -> dueDate
                            else -> fallbackDate
                        }

                        while (eventDateStartTime.month < monthSelectionLocalDate.month) {
                            eventDateStartTime = LocalDateTime(
                                eventDateStartTime.date.plus(2, DateTimeUnit.WEEK),
                                eventDateStartTime.time
                            )
                            eventDateEndTime = LocalDateTime(
                                eventDateEndTime.date.plus(2, DateTimeUnit.WEEK),
                                eventDateEndTime.time
                            )
                        }

                        while (eventDateStartTime.month > monthSelectionLocalDate.month) {
                            eventDateStartTime = LocalDateTime(
                                eventDateStartTime.date.plus(-2, DateTimeUnit.WEEK),
                                eventDateStartTime.time
                            )
                            eventDateEndTime = LocalDateTime(
                                eventDateEndTime.date.plus(-2, DateTimeUnit.WEEK),
                                eventDateEndTime.time
                            )
                        }

                        endDate?.let {
                            while (eventDateStartTime.date <= endDate) {
                                addEventForAssignee(
                                    job,
                                    eventDateStartTime,
                                    eventDateEndTime,
                                    assigneeName,
                                    listOfPlans,
                                    employeeId = employeeId,
                                    mergeEvents = true
                                )

                                eventDateStartTime = LocalDateTime(
                                    eventDateStartTime.date.plus(
                                        2,
                                        DateTimeUnit.WEEK
                                    ), eventDateStartTime.time
                                )
                                eventDateEndTime = LocalDateTime(
                                    eventDateEndTime.date.plus(2, DateTimeUnit.WEEK),
                                    eventDateEndTime.time
                                )
                            }
                        }
                    }

                    Repeat.every_month.name -> {
                        var eventDateStartTime = startDateStartTime
                        var eventDateEndTime = startDateEndTime
                        val dueDate = dueDateTime?.date
                        val fallbackDate = DateTimeUtil.getLastDayOfMonth(monthSelectionLocalDate)
                        val endDate = when {
                            dueDate != null && fallbackDate != null -> minOf(dueDate, fallbackDate)
                            dueDate != null -> dueDate
                            else -> fallbackDate
                        }

                        while (eventDateStartTime.month < monthSelectionLocalDate.month) {
                            eventDateStartTime =
                                DateTimeUtil.getLocalDateTimeMonthsAhead(eventDateStartTime, 1)
                            eventDateEndTime =
                                DateTimeUtil.getLocalDateTimeMonthsAhead(eventDateEndTime, 1)
                        }

                        while (eventDateStartTime.month > monthSelectionLocalDate.month) {
                            eventDateStartTime =
                                DateTimeUtil.getLocalDateTimeMonthsAhead(eventDateStartTime, -1)
                            eventDateEndTime =
                                DateTimeUtil.getLocalDateTimeMonthsAhead(eventDateEndTime, -1)
                        }

                        endDate?.let {
                            while (eventDateStartTime.date <= endDate) {
                                addEventForAssignee(
                                    job,
                                    eventDateStartTime,
                                    eventDateEndTime,
                                    assigneeName,
                                    listOfPlans,
                                    employeeId = employeeId,
                                    mergeEvents = true
                                )
                                eventDateStartTime =
                                    DateTimeUtil.getLocalDateTimeMonthsAhead(eventDateStartTime, 1)
                                eventDateEndTime =
                                    DateTimeUtil.getLocalDateTimeMonthsAhead(eventDateEndTime, 1)
                            }
                        }
                    }
                }
            }
        }
    }

    private fun addEventForAssignee(
        job: Job,
        eventDateStartTime: LocalDateTime,
        eventDateEndTime: LocalDateTime?,
        assigneeName: String,
        listOfPlans: MutableList<Plan>,
        allowOverlap: Boolean = true,
        employeeId: String?,
        mergeEvents: Boolean
    ) {
        var plan = listOfPlans.firstOrNull { it.person == assigneeName }
        if (plan == null && mergeEvents) {
            plan = listOfPlans.firstOrNull { it.events.any { event -> event.jobId == job.id } }
        }
        val newEvent = Event(
            job.id,
            EventStatusMapper.eventStatus(enumValueOf(job.status ?: JobStatus.draft.name)),
            eventDateStartTime,
            eventDateEndTime,
            job.jobTitle,
            StatusColor.getStatusColor(job.status)
        )

        // For the employee, same job might arrive for the team assignment
        val alreadyExists = plan?.events?.any {
            it.jobId == job.id &&
                    it.from == newEvent.from &&
                    it.to == newEvent.to
        } == true
        if (!alreadyExists || employeeId == null) {
            if (plan == null || (!allowOverlap && plan.events.any { it.to == null || newEvent.from.time < it.to!!.time })) {
                log.d("ScheduleUtil") { "addEventForAssignee new Plan: $assigneeName : ${newEvent.title}" }
                listOfPlans.add(Plan(person = assigneeName, events = arrayListOf(newEvent)))
            } else {
                log.d("ScheduleUtil") { "addEventForAssignee: $assigneeName : ${newEvent.title}" }
                plan.events.add(newEvent)
            }
        } else {
            log.d("ScheduleUtil") { "addEventForAssignee alreadyExists: $alreadyExists  employeeId: $employeeId" }
        }
    }

    @Composable
    fun getRepeatIntervalDaily(): Map<String, String> {
        return mapOf(
            stringResource(Res.string.repeat_daily_weekday) to WeekdayOption.EVERY_WEEKDAY.stringRes,
            stringResource(Res.string.repeat_daily_workday) to WeekdayOption.EVERY_WORKING_DAY.stringRes
        )
    }
}
